const DB_NAME = 'ds-whiteboard-drafts';
export const DB_VERSION = 1;
export const DRAFTS_STORE = 'drafts';
export const RECORDS_STORE = 'records';
export const ASSETS_STORE = 'assets';

const PROBE_KEY = '__probe__';
export const OPEN_TIMEOUT_MS = 5000;

// Safari in a private window and a version change held open by another tab both leave
// indexedDB.open() silent forever, which would hang the join saga.
export const openDraftDb = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    let settled = false;
    let timer: ReturnType<typeof setTimeout>;

    const settle = (run: () => void) => {
      clearTimeout(timer);
      settled = true;
      run();
    };

    timer = setTimeout(
      () => settle(() => reject(new Error('indexedDB.open timed out'))),
      OPEN_TIMEOUT_MS
    );

    // An open that completes after we gave up would leave a connection nobody closes, and that
    // connection is exactly what makes the next version upgrade fire `blocked`.
    request.onsuccess = () => {
      if (settled) {
        request.result.close();
        return;
      }

      const db = request.result;

      // Without this a tab left open on an older version blocks every other tab's upgrade, and
      // drafts stay off there until the old tab is closed.
      db.onversionchange = () => db.close();

      settle(() => resolve(db));
    };

    request.onupgradeneeded = () => {
      const db = request.result;

      // Nothing here has shipped, so a version change drops the stores rather than migrating.
      [DRAFTS_STORE, RECORDS_STORE, ASSETS_STORE].forEach((name) => {
        if (db.objectStoreNames.contains(name)) {
          db.deleteObjectStore(name);
        }
      });

      db.createObjectStore(DRAFTS_STORE, { keyPath: 'roomKey' });

      // The board is part of the key, not just a column: every board names its first two records
      // `document:document` and `page:page`, so without it one board's rows overwrite another's.
      const records = db.createObjectStore(RECORDS_STORE, {
        keyPath: ['roomKey', 'whiteboardId', 'recordId'],
      });
      records.createIndex('by-roomKey', 'roomKey');
      records.createIndex('by-board', ['roomKey', 'whiteboardId']);

      const assets = db.createObjectStore(ASSETS_STORE, {
        keyPath: ['roomKey', 'whiteboardId', 'assetId'],
      });
      assets.createIndex('by-roomKey', 'roomKey');
      assets.createIndex('by-board', ['roomKey', 'whiteboardId']);
    };

    request.onerror = () => settle(() => reject(request.error));
    request.onblocked = () => settle(() => reject(new Error('indexedDB.open blocked')));
  });

export const probeWrite = (db: IDBDatabase) =>
  new Promise<void>((resolve, reject) => {
    const tx = db.transaction(DRAFTS_STORE, 'readwrite');
    const store = tx.objectStore(DRAFTS_STORE);

    store.put({ roomKey: PROBE_KEY });
    store.delete(PROBE_KEY);

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
